import React, { useContext, createContext, useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  checkProfileCompleteness,
  evaluateProfileCompleteness,
  fetchUserProfile,
  updateUserProfile,
} from '../firebase/profileService';
import { shouldBootstrapSocialProfile } from '../features/onboarding/onboardingHelpers';
import { buildLegacyFavoriteFightersMigration } from '../utils/favoritesMigration';
import { isIdentitySwap, isStaleSessionLoad } from './sessionIdentity';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [profileCheckLoading, setProfileCheckLoading] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  // AUTH-22: once complete in this session, never regress to incomplete on a
  // transient profile read / token-refresh re-run of onAuthStateChanged.
  const profileCompleteOnceRef = useRef(false);
  // VAULT-37/38: drop stale writes when auth identity changes mid-hydrate.
  const sessionGenRef = useRef(0);
  const loadedUidRef = useRef(null);

  const markProfileComplete = (value) => {
    if (value === true) {
      profileCompleteOnceRef.current = true;
    }
    setIsProfileComplete(profileCompleteOnceRef.current || value === true);
  };

  useEffect(() => {
    let retryTimeout;
    let cancelled = false;
    const coldStartCeiling = setTimeout(() => {
      if (cancelled) return;
      console.warn('Auth cold-start ceiling hit — unblocking shell');
      setIsLoading(false);
      setProfileCheckLoading(false);
      setAuthInitialized(true);
    }, 8000);

    const withTimeout = (promise, ms, label) =>
      Promise.race([
        promise,
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
        }),
      ]);

    const loadAuthenticatedUser = async (user, gen) => {
      const isStale = () =>
        isStaleSessionLoad({
          cancelled,
          gen,
          currentGen: sessionGenRef.current,
          expectedUid: user.uid,
          liveUid: auth.currentUser?.uid,
        });

      setProfileCheckLoading(true);

      try {
        let userRoleDoc;
        let retryCount = 0;
        const maxRetries = 3;

        while (retryCount < maxRetries) {
          try {
            userRoleDoc = await withTimeout(
              getDoc(doc(db, 'userRoles', user.uid)),
              8000,
              'userRoles getDoc'
            );
            break;
          } catch (firestoreError) {
            console.warn(`Firestore connection attempt ${retryCount + 1} failed:`, firestoreError);
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount));
            } else {
              console.warn('All Firestore connection attempts failed, continuing with cached user state');
            }
          }
        }

        let profile;
        let profileFetchFailed = false;
        try {
          profile = await withTimeout(fetchUserProfile(user.uid), 8000, 'fetchUserProfile');
        } catch (profileError) {
          console.warn('Could not fetch user profile:', profileError);
          profile = null;
          profileFetchFailed = true;
        }

        if (isStale()) return;

        // AUTH-22: failed/timed-out profile read must NOT evaluate as incomplete.
        if (profileFetchFailed && !profile) {
          console.warn('AUTH-22: profile fetch failed — keeping session out of wizard');
          markProfileComplete(true);
          return;
        }

        if (userRoleDoc && userRoleDoc.exists()) {
          setUserRole(userRoleDoc.data().role);
        } else if (profile?.role) {
          setUserRole(profile.role);
        } else {
          setUserRole(null);
        }

        if (user.providerData.length > 0) {
          const provider = user.providerData[0];
          if (shouldBootstrapSocialProfile({ profile, profileFetchFailed, providerId: provider.providerId })) {
            let firstName = '';
            let lastName = '';
            if (user.displayName) {
              const nameParts = user.displayName.split(' ');
              firstName = nameParts[0] || '';
              lastName = nameParts.slice(1).join(' ') || '';
            }

            const newProfileData = {
              email: user.email,
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
              profileImageUrl: user.photoURL || '',
              firstName,
              lastName,
              bio: '',
              lastLogin: new Date(),
              lastLoginAt: new Date(),
              profileCompleted: false,
              registrationProvider: provider.providerId,
              onboarding: { status: 'in_progress', step: 0, version: 1, startedAt: new Date() },
            };

            try {
              await withTimeout(
                updateUserProfile(user.uid, newProfileData),
                10000,
                'create social profile'
              );
              profile = { id: user.uid, ...newProfileData, username: '' };
              if (isStale()) return;
              setUserProfile(profile);
              loadedUidRef.current = user.uid;
              profileCompleteOnceRef.current = false;
              setIsProfileComplete(false);
              return;
            } catch (createProfileError) {
              console.warn('Could not create new user profile:', createProfileError);
            }
          }
        }

        if (profile && (profile.emailVerified !== user.emailVerified || !profile.lastLogin)) {
          try {
            const loginPatch = {
              emailVerified: user.emailVerified,
              lastLogin: new Date(),
              lastLoginAt: new Date(),
              updatedAt: new Date(),
            };
            await withTimeout(
              updateDoc(doc(db, 'users', user.uid), loginPatch),
              8000,
              'login tracking update'
            );
            profile = { ...profile, ...loginPatch };
          } catch (error) {
            console.warn('Error updating login tracking (non-critical):', error);
          }
        }

        if (isStale()) return;
        setUserProfile(profile);
        loadedUidRef.current = user.uid;

        const favoritesMigration = buildLegacyFavoriteFightersMigration(profile);
        if (favoritesMigration) {
          updateUserProfile(user.uid, favoritesMigration).catch((migrationErr) => {
            console.warn('Legacy favorites migration failed (non-critical):', migrationErr);
          });
        }

        try {
          const profileCompletion = evaluateProfileCompleteness(user, profile);
          if (profileCompletion.shouldMarkComplete) {
            updateUserProfile(user.uid, {
              profileCompleted: true,
              updatedAt: new Date(),
            }).catch(() => {});
          }
          markProfileComplete(profileCompletion.isComplete === true);
        } catch (profileCompletionError) {
          console.warn('Could not check profile completion:', profileCompletionError);
          markProfileComplete(true);
        }
      } finally {
        if (!isStale()) {
          setProfileCheckLoading(false);
          setAuthInitialized(true);
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const gen = ++sessionGenRef.current;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
      }
      clearTimeout(coldStartCeiling);

      setCurrentUser(user);
      setIsLoading(false);

      if (user) {
        if (isIdentitySwap(loadedUidRef.current, user.uid)) {
          loadedUidRef.current = null;
          profileCompleteOnceRef.current = false;
          setUserRole(null);
          setUserProfile(null);
          setIsProfileComplete(false);
        }
        setProfileCheckLoading(true);
        try {
          await loadAuthenticatedUser(user, gen);
        } catch (error) {
          if (sessionGenRef.current !== gen) return;
          console.error('Error fetching user data:', error);

          const isTransient =
            error.code === 'unavailable' ||
            (error.message &&
              (error.message.includes('network') ||
                error.message.includes('connection') ||
                error.message.includes('Cross-Origin-Opener-Policy') ||
                error.message.includes('timed out')));

          if (isTransient) {
            console.warn('Network/connection error detected, maintaining user session');
            markProfileComplete(true);
            setProfileCheckLoading(false);
            setAuthInitialized(true);

            retryTimeout = setTimeout(() => {
              const current = auth.currentUser;
              if (!current || cancelled || sessionGenRef.current !== gen) return;
              const retryGen = ++sessionGenRef.current;
              console.log('Retrying authentication data fetch...');
              loadAuthenticatedUser(current, retryGen).catch((retryErr) => {
                console.warn('Auth profile retry failed:', retryErr);
              });
            }, 10000);
          } else {
            setUserRole(null);
            setUserProfile(null);
            markProfileComplete(true);
            setProfileCheckLoading(false);
            setAuthInitialized(true);
          }
        }
      } else {
        loadedUidRef.current = null;
        profileCompleteOnceRef.current = false;
        setUserRole(null);
        setUserProfile(null);
        setIsProfileComplete(false);
        setProfileCheckLoading(false);
        setAuthInitialized(true);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(coldStartCeiling);
      unsubscribe();
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  const refreshProfileCompletion = async () => {
    if (currentUser) {
      setProfileCheckLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const profile = await fetchUserProfile(currentUser.uid);
        setUserProfile(profile);

        const profileCompletion = await checkProfileCompleteness(profile);
        const isComplete = profileCompletion.isComplete === true;
        if (isComplete) {
          profileCompleteOnceRef.current = true;
        }
        setIsProfileComplete(profileCompleteOnceRef.current || isComplete);
        return isComplete;
      } catch (error) {
        console.error('Error checking profile completion:', error);
        return false;
      } finally {
        setProfileCheckLoading(false);
      }
    }
    return false;
  };

  // AUTH-32: EmailVerificationBanner "Check Status" / resend flow
  const refreshUser = async () => {
    const user = auth.currentUser;
    if (!user) return null;
    await user.reload();
    const refreshed = auth.currentUser;
    setCurrentUser(refreshed);
    if (refreshed) {
      try {
        const profile = await fetchUserProfile(refreshed.uid);
        setUserProfile(profile);
        if (profile && refreshed.emailVerified && profile.emailVerified !== true) {
          await updateDoc(doc(db, 'users', refreshed.uid), {
            emailVerified: true,
            updatedAt: new Date(),
          }).catch(() => {});
        }
      } catch (err) {
        console.warn('refreshUser profile sync failed:', err);
      }
    }
    return refreshed;
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await updateDoc(doc(db, 'users', user.uid), {
        lastLogin: new Date(),
        lastLoginAt: new Date(),
        emailVerified: user.emailVerified,
        updatedAt: new Date(),
      });
      return userCredential;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    profileCompleteOnceRef.current = false;
    loadedUidRef.current = null;
    await signOut(auth);
  };

  const value = {
    currentUser,
    userRole,
    userProfile,
    isProfileComplete,
    profileCheckLoading,
    authInitialized,
    refreshProfileCompletion,
    refreshUser,
    login,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
